<template>
    <div class="box" v-if="hasAccess">
        <div class="box-header">
            <h6 class="title-color inline">Sprawa źródłowa (CRM)</h6>
            <a class="pull-right" title="Powiąż sprawę" @click.prevent="toggleSearch">
                <i class="material-icons md-18 text-blue">{{ showSearch ? 'close' : 'add' }}</i>
            </a>
        </div>
        <div class="m-a-0 custom-divider"/>
        <div class="box-body">
            <rotate-square2 style="margin: 10px;" v-if="loading"/>
            <span class="text-muted text-xs" v-if="!loading && linkedTickets.length === 0 && !showSearch">Brak powiązanej sprawy</span>

            <div v-for="t in linkedTickets" :key="t.ticketId" class="linked-ticket-row">
                <div class="lt-main">
                    <a :href="ticketHref(t.ticketId)" target="_blank" rel="noopener" class="_600 lt-link" :title="t.subject">
                        <span class="lt-num">{{ t.ticketNumber }}</span>
                        <span class="lt-subject">{{ t.subject || '(brak tematu)' }}</span>
                    </a>
                    <a class="text-grey lt-remove" href="" @click.prevent="unlink(t)" title="Usuń powiązanie">
                        <i class="material-icons md-18">highlight_off</i>
                    </a>
                </div>
                <div class="lt-meta">
                    <span class="lt-source">{{ sourceLabel(t.source) }}</span>
                    <span v-if="t.auto" class="lt-auto" title="Powiązanie utworzone automatycznie">auto</span>
                    <span v-if="t.customerName" class="text-muted text-xs">{{ t.customerName }}</span>
                </div>
            </div>

            <div v-if="showSearch" class="lt-search">
                <label class="lt-label">Źródło zamówienia</label>
                <select class="form-control input-sm" v-model="linkSource">
                    <option value="email">E-mail</option>
                    <option value="phone">Telefon</option>
                    <option value="sms">SMS</option>
                    <option value="pdf">PDF / skan</option>
                    <option value="offer">Z oferty</option>
                    <option value="direct">Bezpośrednie</option>
                    <option value="other">Inne</option>
                </select>
                <input type="text" class="form-control input-sm m-t-xs" placeholder="Szukaj sprawy (numer, temat, klient)..."
                    v-model="query" @input="search" />
                <div v-if="searching" class="text-muted text-xs m-t-xs">Szukam...</div>
                <div v-else-if="results.length > 0" class="lt-results m-t-xs">
                    <div v-for="r in results" :key="r._id" class="lt-result-item" @click="link(r)">
                        <strong>{{ r.ticketNumber }}</strong>
                        <span class="text-muted text-xs m-l-xs">{{ r.subject }}</span>
                        <span class="text-muted text-xs d-block">{{ r.customerName || '' }}</span>
                    </div>
                </div>
                <div v-else-if="(query || '').trim().length >= 2 && !searching" class="text-muted text-xs m-t-xs">Brak wyników.</div>
            </div>
        </div>
    </div>
</template>

<script>
import Api from '../../Api.js';
import UserManager from '../../UserManager.js';
import { RotateSquare2 } from 'vue-loading-spinner';

export default {
    name: 'LinkedTickets',
    props: { order: Object },
    components: { RotateSquare2 },
    data() {
        return {
            // Powiązanie sprawa<->ZO żyje w crm-service (Ticket.relatedOrders);
            // ten panel pobiera je stamtąd po orderId (kompozycja we froncie).
            linkedTickets: [],
            loading: false,
            showSearch: false,
            query: '',
            results: [],
            searching: false,
            _searchTimer: null,
            linkSource: 'other',
            saving: false
        };
    },
    computed: {
        hasAccess() {
            return UserManager.hasPermission('view.crm') || UserManager.hasPermission('imap.messages.view');
        }
    },
    created() {
        this.load();
    },
    methods: {
        async load() {
            if (!this.order || !this.order._id || !this.hasAccess) return;
            this.loading = true;
            try {
                const res = await Api.imap.tickets.byOrder(this.order._id);
                this.linkedTickets = (res && res.items) ? res.items : [];
            } catch (e) {
                this.linkedTickets = [];
            }
            this.loading = false;
        },
        sourceLabel(source) {
            const map = { email: 'E-mail', phone: 'Telefon', sms: 'SMS', pdf: 'PDF', offer: 'Oferta', direct: 'Bezpośrednie', other: 'Inne' };
            return map[source] || 'Inne';
        },
        ticketHref(id) {
            if (!id) return '#';
            try {
                const href = this.$router.resolve('/crm/tickets/' + id).href;
                return href || ('#/crm/tickets/' + id);
            } catch (e) {
                return '#/crm/tickets/' + id;
            }
        },
        toggleSearch() {
            this.showSearch = !this.showSearch;
            if (!this.showSearch) {
                this.query = '';
                this.results = [];
            }
        },
        search() {
            clearTimeout(this._searchTimer);
            const q = (this.query || '').trim();
            if (q.length < 2) { this.results = []; return; }
            this._searchTimer = setTimeout(async () => {
                this.searching = true;
                try {
                    const res = await Api.imap.tickets.list({ search: q, searchEverywhere: true, limit: 20 });
                    this.results = (res && res.items) ? res.items : [];
                } catch (e) {
                    this.results = [];
                }
                this.searching = false;
            }, 300);
        },
        async link(ticket) {
            if (!this.order || !this.order._id || this.saving) return;
            this.saving = true;
            try {
                await Api.imap.tickets.linkOrder(ticket._id, {
                    orderId: this.order._id,
                    source: this.linkSource
                });
                this.showSearch = false;
                this.query = '';
                this.results = [];
                await this.load();
                this.$notify({ group: 'global', type: 'notification-success', text: 'Powiązano sprawę ' + (ticket.ticketNumber || '') });
            } catch (e) {
                this.$notify({ group: 'global', type: 'notification-error', text: 'Nie udało się powiązać sprawy.' });
            }
            this.saving = false;
        },
        async unlink(t) {
            if (!this.order || !this.order._id) return;
            if (!await this.$h.confirm('Usunąć powiązanie ze sprawą ' + (t.ticketNumber || '') + '?')) return;
            try {
                await Api.imap.tickets.unlinkOrder(t.ticketId, this.order._id);
                await this.load();
                this.$notify({ group: 'global', type: 'notification-success', text: 'Usunięto powiązanie.' });
            } catch (e) {
                this.$notify({ group: 'global', type: 'notification-error', text: 'Wystąpił błąd podczas usuwania.' });
            }
        }
    }
};
</script>

<style scoped>
.linked-ticket-row { padding: 4px 0; border-bottom: 1px solid #f1f3f5; }
.linked-ticket-row:last-child { border-bottom: none; }
.lt-main { display: flex; align-items: center; gap: 6px; }
.lt-link { flex: 1; min-width: 0; text-decoration: none; display: flex; align-items: center; gap: 6px; }
.lt-num { font-family: monospace; font-size: 11px; color: #6b7280; }
.lt-subject { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lt-remove { flex-shrink: 0; }
.lt-meta { display: flex; align-items: center; gap: 6px; margin-top: 2px; }
.lt-source { font-size: 10px; color: #6b7280; }
.lt-auto {
    font-size: 9px; font-weight: 600; text-transform: uppercase;
    padding: 1px 5px; border-radius: 8px; background: #dbeafe; color: #1e40af;
}
.lt-search { margin-top: 8px; }
.lt-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #6c757d; font-weight: 600; margin-bottom: 3px; display: block; }
.lt-results { border: 1px solid #dee2e6; border-radius: 4px; max-height: 160px; overflow-y: auto; background: #fff; }
.lt-result-item { padding: 6px 10px; cursor: pointer; font-size: 12px; border-bottom: 1px solid #f1f3f5; }
.lt-result-item:hover { background: #f0f6ff; }
.m-t-xs { margin-top: 6px; }
.m-l-xs { margin-left: 6px; }
.d-block { display: block; }

/* ---------- Tryb ciemny ---------- */
.theme-dark .linked-ticket-row { border-bottom-color: var(--dm-border); }
.theme-dark .lt-link { color: var(--dm-link); }
.theme-dark .lt-link:hover { color: var(--dm-link-hover); }
.theme-dark .lt-num { color: var(--dm-text-muted); }
.theme-dark .lt-source { color: var(--dm-text-muted); }
.theme-dark .lt-label { color: var(--dm-text-muted); }
.theme-dark .lt-auto {
    background: var(--dm-tint-blue);
    color: var(--dm-on-blue);
}
.theme-dark .lt-results {
    background: var(--dm-surface-2);
    border-color: var(--dm-border);
}
.theme-dark .lt-result-item { border-bottom-color: var(--dm-border); color: var(--dm-text); }
.theme-dark .lt-result-item:hover { background: var(--dm-surface-3); }
</style>
